// libs
import { useMutation } from '@apollo/client/react'
// hooks
import { useModalData } from 'hooks/useShowModal'
// constants
import { Modals, ENTITIES } from 'constants/index'
// graphql
import { FIELD_VALUE_UPSERT_MUTATION } from 'graphql/objects/mutations'
import { REVIEW_CREATE_MUTATION, REVIEW_UPDATE_MUTATION } from 'graphql/reviews/mutations'
// types
import { IReview, IReviewResponse } from 'types/graphql'
// utils
import get from 'lodash/get'
import moment from 'moment'
import { getRefetchQueriesForReviews, mapFormPicklistsToFieldValues } from 'utils/reviews'

type ReviewCreateResponse = {
  reviewCreate: IReviewResponse
}

type ReviewMutationParams = {
  data?: any
  status: string
}

// mutations for the review opened in the review drawer (REVIEW_CREATE_MODAL)
export const useReviewMutations = () => {
  const [reviewCreateMutation] = useMutation<ReviewCreateResponse>(REVIEW_CREATE_MUTATION)
  const [reviewUpdateMutation] = useMutation(REVIEW_UPDATE_MUTATION)
  const [fieldValueUpsertMutation] = useMutation(FIELD_VALUE_UPSERT_MUTATION)

  const {
    modalData: { entity, review }
  } = useModalData({ modalType: Modals.REVIEW_CREATE_MODAL })

  const upsertPicklistValues = async (reviewId: string, data, refetchQueries, fieldValueMap = []) => {
    const input = mapFormPicklistsToFieldValues(data, fieldValueMap)

    if (!reviewId || !input.length) {
      return
    }

    await fieldValueUpsertMutation({ variables: { entityId: reviewId, input }, refetchQueries })
  }

  const getReviewInput = (data, status: string) => ({
    dueAt: review.dueDate.toISOString(),
    startPeriod: moment(review.startPeriod).toISOString(),
    endPeriodAt: moment(review.endPeriod).toISOString(),
    planId: entity.entityType === ENTITIES.PLAN ? entity.id : null,
    planObjectiveId: entity.entityType === ENTITIES.PLAN_OBJECTIVE ? entity.id : null,
    planActionId: entity.entityType === ENTITIES.PLAN_ACTION ? entity.id : null,
    planKPIId: entity.entityType === ENTITIES.PLAN_KPI ? entity.id : null,
    status,
    values: {
      goals: get(data, 'goals'),
      doWell: get(data, 'doWell'),
      doBetter: get(data, 'doBetter')
    }
  })

  const createReview = async ({ data, status }: ReviewMutationParams): Promise<IReview> => {
    const refetchQueries = getRefetchQueriesForReviews(entity)

    const {
      data: {
        reviewCreate: { data: createdReview }
      }
    } = await reviewCreateMutation({
      variables: {
        input: { ...getReviewInput(data, status), scheduledReviewId: review.scheduledReview.id }
      },
      refetchQueries
    })

    await upsertPicklistValues(get(createdReview, 'id'), data, refetchQueries)

    return createdReview
  }

  const updateReview = async ({ data, status }: ReviewMutationParams) => {
    const refetchQueries = getRefetchQueriesForReviews(entity)

    await reviewUpdateMutation({
      variables: { id: review.id, input: getReviewInput(data, status) },
      refetchQueries
    })

    await upsertPicklistValues(review.id, data, refetchQueries, review.fieldValueMap)
  }

  return { createReview, updateReview }
}
